import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { DEFAULT_LOCALE, isLocale, matchSupportedLocale, type Locale } from './locale.js';

/**
 * `sessionStorage`-Key für die persistierte Locale-Wahl (Spec-Vorgabe).
 */
export const LOCALE_STORAGE_KEY = 'pinkCancel.locale';

/**
 * Liest die aktuell hinterlegte Locale aus `sessionStorage`. Defensiv: wirft
 * nicht, wenn Storage blockiert ist (z. B. Safari Private Mode) — gibt dann
 * `undefined` zurück, der Aufrufer fällt auf den Default zurück.
 */
function readStoredLocale(): Locale | undefined {
  try {
    const raw = window.sessionStorage.getItem(LOCALE_STORAGE_KEY);
    return isLocale(raw) ? raw : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Schreibt die gewählte Locale nach `sessionStorage`. Schluckt Storage-Fehler,
 * weil die UI in dem Fall trotzdem sinnvoll funktioniert.
 *
 * @param locale Zu speichernde Locale.
 */
function writeStoredLocale(locale: Locale): void {
  try {
    window.sessionStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // Storage nicht verfügbar — Sprachwahl überlebt Reload nicht, das ist okay.
  }
}

/**
 * Liest die bevorzugte Sprache aus der Browser-Umgebung (`navigator`). Wertet
 * `navigator.languages` in Präferenzreihenfolge aus (Fallback auf
 * `navigator.language`) und gibt die erste unterstützte Locale zurück. Liefert
 * `undefined`, wenn keine Browser-Sprache passt oder `navigator` fehlt.
 */
function readBrowserLocale(): Locale | undefined {
  if (typeof navigator === 'undefined') {
    return undefined;
  }
  const preferredTags =
    navigator.languages !== undefined && navigator.languages.length > 0
      ? navigator.languages
      : navigator.language
        ? [navigator.language]
        : [];
  for (const tag of preferredTags) {
    const matched = matchSupportedLocale(tag);
    if (matched !== undefined) {
      return matched;
    }
  }
  return undefined;
}

/**
 * Liefert den initialen Locale-Wert in Prioritätsreihenfolge:
 * persistierte Wahl (`sessionStorage`) > Browser-Sprache > Default (`DE`).
 */
export function readInitialLocale(): Locale {
  return readStoredLocale() ?? readBrowserLocale() ?? DEFAULT_LOCALE;
}

/**
 * Hook für Locale-Zugriff + Persistenz.
 *
 * **i18next ist die einzige Quelle der Wahrheit.** `locale` wird aus
 * `i18n.language` abgeleitet statt in lokalem `useState` gehalten — mehrere
 * `useLanguage()`-Instanzen (Sprachumschalter, Cancel-Flow) bleiben so
 * automatisch synchron. Ein lokaler State pro Instanz hatte zur Folge, dass
 * der DE/EN-Switch zwar alle `t()`-Texte umstellte, die formatierte
 * Termin-Anzeige aber mit der Start-Locale hängen blieb.
 *
 * Die Start-Locale entscheidet `main.tsx` einmalig über `readInitialLocale()`
 * und initialisiert i18next damit; der Hook liest sie nicht erneut.
 */
interface UseLanguageResult {
  readonly locale: Locale;
  readonly setLocale: (next: Locale) => void;
}

export function useLanguage(): UseLanguageResult {
  const { i18n } = useTranslation();
  const locale = matchSupportedLocale(i18n.language) ?? DEFAULT_LOCALE;

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = locale;
    }
  }, [locale]);

  const setLocale = useCallback(
    (next: Locale) => {
      void i18n.changeLanguage(next);
      writeStoredLocale(next);
    },
    [i18n],
  );

  return { locale, setLocale };
}
